import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { RiCloseLargeFill } from "react-icons/ri";
import ImageTemplateServices from "../../../services/ImageTemplateServices";
import BoardTabs, { yantrasForTab } from "./BoardTabs";

const api = new ImageTemplateServices();

const fileBase = () =>
  `${process.env.REACT_APP_NODE_URL || ""}/api/yantra`;

const EditImageTemplate = ({
  show,
  onHide,
  yantras,
  games = [],
  templateId,
  onSaved,
}) => {
  const previewUrlsRef = useRef({});
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("all");
  const [initialName, setInitialName] = useState("");
  const [existingByYantra, setExistingByYantra] = useState({});
  const [initialYantraNames, setInitialYantraNames] = useState({});

  const safeYantras = useMemo(
    () => (Array.isArray(yantras) ? yantras : []),
    [yantras],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        templateName: Yup.string()
          .trim()
          .required("Template name is required")
          .max(255, "Max 255 characters"),
        // Names and images are per-yantra overrides: any yantra left alone
        // simply keeps its default. A template with nothing left in it is
        // the only thing refused.
        yantraNames: Yup.object(),
        files: Yup.object().test(
          "at-least-one-image",
          "Keep or upload at least one yantra image",
          (value, ctx) => {
            const removed = ctx.parent.removed || [];
            return safeYantras.some((y) => {
              const hasNew = value && value[y.id] instanceof File;
              const kept = !!existingByYantra[y.id] && !removed.includes(y.id);
              return hasNew || kept;
            });
          },
        ),
        removed: Yup.array().of(Yup.number()),
      }),
    [safeYantras, existingByYantra],
  );

  const revokeAll = () => {
    Object.values(previewUrlsRef.current).forEach((u) => {
      if (u && String(u).startsWith("blob:")) {
        try {
          URL.revokeObjectURL(u);
        } catch (_) {}
      }
    });
    previewUrlsRef.current = {};
  };

  useEffect(() => {
    if (!show || !templateId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      revokeAll();
      try {
        const res = await api.getDetail(templateId);
        if (cancelled) return;
        if (res.isSuccess && res.data?.template) {
          setInitialName(res.data.template.template_name || "");
          const imgMap = {};
          const nameMap = {};
          (res.data.items || []).forEach((row) => {
            imgMap[row.yantra_id] = row.image_filename;
            nameMap[row.yantra_id] = row.yantra_name || "";
          });
          setExistingByYantra(imgMap);
          setInitialYantraNames(nameMap);
        } else {
          toast.error(res.message || "Failed to load");
          onHide();
        }
      } catch (e) {
        if (!cancelled) toast.error("Failed to load template");
        onHide();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, templateId, onHide]);

  useEffect(() => {
    if (show) return;
    revokeAll();
    setTab("all");
    setInitialName("");
    setExistingByYantra({});
    setInitialYantraNames({});
  }, [show]);

  useEffect(() => () => revokeAll(), []);

  const setPreview = (yantraId, file) => {
    const old = previewUrlsRef.current[yantraId];
    if (old && String(old).startsWith("blob:")) URL.revokeObjectURL(old);
    if (file) {
      previewUrlsRef.current[yantraId] = URL.createObjectURL(file);
    } else {
      delete previewUrlsRef.current[yantraId];
    }
  };

  const initialValues = useMemo(
    () => ({
      templateName: initialName,
      yantraNames: Object.fromEntries(
        safeYantras.map((y) => [y.id, initialYantraNames[y.id] ?? ""])
      ),
      files: {},
      removed: [],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialName, initialYantraNames],
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      backdrop="static"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          try {
            const fd = new FormData();
            fd.append("template_name", values.templateName.trim());
            if (values.removed.length) {
              fd.append(
                "remove_yantra_ids",
                JSON.stringify(values.removed),
              );
            }
            Object.entries(values.yantraNames || {}).forEach(([yantraId, name]) => {
              fd.append(`yantra_name_${yantraId}`, String(name || "").trim());
            });
            Object.entries(values.files).forEach(([yantraId, file]) => {
              if (file instanceof File) fd.append(`yantra_${yantraId}`, file);
            });
            const res = await api.updateTemplate(templateId, fd);
            if (res.isSuccess) {
              toast.success(res.message || "Template updated");
              onSaved?.();
              resetForm();
              revokeAll();
              onHide();
            } else {
              toast.error(res.message || "Update failed");
            }
          } catch (err) {
            toast.error(
              err?.response?.data?.message || err?.message || "Update failed",
            );
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          submitCount,
        }) => {
          const handleYantraName = (yantraId, val) => {
            setFieldValue("yantraNames", { ...values.yantraNames, [yantraId]: val });
            setFieldTouched("yantraNames", true, false);
          };
          const displayUrl = (yantraId) => {
            const localFile = values.files[yantraId];
            if (localFile) return previewUrlsRef.current[yantraId];
            const fn = existingByYantra[yantraId];
            if (fn && !values.removed.includes(yantraId)) {
              return `${fileBase()}/${encodeURIComponent(fn)}`;
            }
            return null;
          };
          const handleFile = (yantraId, file) => {
            if (!file) return;
            setPreview(yantraId, file);
            setFieldValue("files", { ...values.files, [yantraId]: file });
            setFieldValue(
              "removed",
              values.removed.filter((id) => id !== yantraId),
            );
            setFieldTouched("files", true, false);
          };
          const markRemove = (yantraId) => {
            setPreview(yantraId, null);
            const nextFiles = { ...values.files };
            delete nextFiles[yantraId];
            setFieldValue("files", nextFiles);
            if (!values.removed.includes(yantraId)) {
              setFieldValue("removed", [...values.removed, yantraId]);
            }
            setFieldTouched("files", true, false);
          };
          const undoRemove = (yantraId) => {
            setFieldValue(
              "removed",
              values.removed.filter((id) => id !== yantraId),
            );
            setFieldTouched("files", true, false);
          };
          const filesError =
            (touched.files || submitCount > 0) && errors.files;
          return (
            <Form>
              <div className="it-modal-head">
                <h5 className="it-modal-title">Edit image template</h5>
                <button
                  type="button"
                  className="it-icon-close"
                  onClick={onHide}
                  aria-label="Close"
                >
                  <RiCloseLargeFill size={20} />
                </button>
              </div>
              <Modal.Body className="it-modal-body">
                {loading ? (
                  <div className="it-loading">Loading…</div>
                ) : (
                  <>
                    <div className="it-field">
                      <label htmlFor="templateName" className="it-label">
                        Template name{" "}
                        <span className="required-asterisk">*</span>
                      </label>
                      <Field
                        id="templateName"
                        name="templateName"
                        placeholder="e.g. template name"
                        maxLength={255}
                        className={`it-input ${
                          errors.templateName && touched.templateName
                            ? "input-error"
                            : ""
                        }`}
                      />
                      <ErrorMessage name="templateName">
                        {(msg) => (
                          <div className="admin_error-text">{msg}</div>
                        )}
                      </ErrorMessage>
                    </div>

                    {filesError && (
                      <div className="admin_error-text it-files-error">
                        {filesError}
                      </div>
                    )}

                    <p className="it-hint">
                      Yantras without an image keep the default. Switch tabs
                      to see each game's board.
                    </p>

                    <BoardTabs
                      games={games}
                      total={safeYantras.length}
                      covered={
                        new Set(
                          safeYantras
                            .filter((y) => {
                              const hasNew = values.files[y.id] instanceof File;
                              const kept =
                                !!existingByYantra[y.id] &&
                                !values.removed.includes(y.id);
                              return hasNew || kept;
                            })
                            .map((y) => Number(y.id)),
                        )
                      }
                      value={tab}
                      onChange={setTab}
                    />

                    <div className="it-yantra-grid">
                      {yantrasForTab(tab, safeYantras, games).map((y, idx) => {
                        const removed = values.removed.includes(y.id);
                        const url = displayUrl(y.id);
                        const hasImage = !!url && !removed;
                        const defaultUrl = y.image
                          ? `${fileBase()}/${encodeURIComponent(y.image)}`
                          : null;
                        return (
                          <div
                            key={y.id}
                            className={`it-yantra-card${hasImage ? " it-yantra-card-set" : ""}`}
                          >
                            <div className="it-yantra-head">
                              <input
                                type="text"
                                className="it-yantra-name-input"
                                placeholder={y.name || "Yantra name"}
                                maxLength={255}
                                value={values.yantraNames?.[y.id] ?? ""}
                                onChange={(e) => handleYantraName(y.id, e.target.value)}
                              />
                              <span className="it-yantra-id"># {idx + 1}</span>
                            </div>
                            {removed ? (
                              <>
                                <div className="it-removed-banner">
                                  <span>Image removed</span>
                                  <button
                                    type="button"
                                    className="it-btn-link"
                                    onClick={() => undoRemove(y.id)}
                                  >
                                    Undo
                                  </button>
                                </div>
                                <label className="it-upload-zone it-mt-8">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="it-file-input"
                                    onChange={(e) =>
                                      handleFile(y.id, e.target.files?.[0])
                                    }
                                  />
                                  <span>Upload new image</span>
                                </label>
                              </>
                            ) : url ? (
                              <div className="it-preview-wrap">
                                <img
                                  src={url}
                                  alt=""
                                  className="it-preview-img"
                                />
                                <div className="it-preview-actions">
                                  <label className="it-btn-secondary it-file-label">
                                    Re-upload
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="it-file-input"
                                      onChange={(e) =>
                                        handleFile(y.id, e.target.files?.[0])
                                      }
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    className="it-btn-danger"
                                    onClick={() => markRemove(y.id)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <label className="it-upload-zone it-upload-zone--default">
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="it-file-input"
                                  onChange={(e) =>
                                    handleFile(y.id, e.target.files?.[0])
                                  }
                                />
                                {defaultUrl && (
                                  <img src={defaultUrl} alt="" className="it-default-img" />
                                )}
                                <span className="it-default-tag">Default</span>
                                <span>Add image</span>
                              </label>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer className="it-modal-footer">
                <button
                  type="button"
                  className="it-btn-ghost"
                  onClick={onHide}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="it-btn-primary"
                  disabled={isSubmitting || loading}
                >
                  {isSubmitting ? "Saving…" : "Save changes"}
                </button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default EditImageTemplate;
